<template>
  <v-layout row justify-center>
    <dialog-wrapper :visible="dialog">
      <v-dialog v-model="dialog" max-width="600">
        <v-tooltip slot="activator" left>
          <v-btn
            slot="activator"
            fixed
            bottom
            right
            fab
            color="primary"
          >
            <v-icon>edit</v-icon>
          </v-btn>
          <span v-t="'settingsContainer.manageUsers'" />
        </v-tooltip>
        <v-card>
          <v-card-title class="headline"><span v-t="'settingsContainer.userEditing'" /></v-card-title>
          <v-card-title class="py-0">
            <v-autocomplete
              v-model="selectedUser"
              :label="$t('settingsContainer.searchByName')"
              prepend-icon="search"
              :loading="loading"
              cache-items
              :item-text="(v) => `${v.displayName} ${v.email}`"
              item-value="id"
              :items="items"
              return-object
              persistent-hint
              clearable
              :no-data-text="$t('dishesContainer.userNotFound')"
              :search-input.sync="searchQuery"
            >
              <template slot="item" slot-scope="data">
                <v-list-tile-content>
                  <v-list-tile-title>{{ data.item.displayName }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{ data.item.email }}</v-list-tile-sub-title>
                </v-list-tile-content>
              </template>
            </v-autocomplete>
          </v-card-title>
          <v-card-title v-if="selectedUser" class="pt-0">
            <v-layout wrap>
              <v-flex xs8>
                <v-text-field v-model="selectedUser.displayName" :label="$t('settingsContainer.displayname')" prepend-icon="face" />
              </v-flex>
              <v-flex :xs4="$vuetify.breakpoint.mdAndUp">
                <v-switch v-model="selectedUser.admin" class="mt-3 ml-1" color="primary" :label="$t('settingsContainer.admin')" />
              </v-flex>
              <v-flex xs8>
                <v-select
                  v-model="selectedUser.cityId"
                  :items="cities"
                  item-value="id"
                  item-text="name"
                  :label="$t('settingsContainer.city')"
                  prepend-icon="location_city"
                />
              </v-flex>
            </v-layout>
          </v-card-title>
          <v-switch
            v-if="selectedUser"
            v-model="selectedUser.active"
            color="primary"
            class="user-disable-switch"
            :label="$t(`settingsContainer.${selectedUser.active ? 'active' : 'inactive'}`)"
          />
          <v-card-actions>
            <v-spacer />
            <v-btn
              v-if="selectedUser"
              color="primary"
              flat
              :loading="savingUser"
              @click="saveUser"
            ><span v-t="'common.save'" /></v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-if="selectedUser"
        v-model="ordersWithoutDepositUndoDialog"
        max-width="500"
        persistent
      >
        <v-card>
          <v-card-text>
            {{ $t('settingsContainer.ordersWithoutDepositUndoText', { displayName: selectedUser.displayName }) }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              flat="flat"
              @click="onOrdersWithoutDepositActiveDialogCancel()"
            >
              {{ $t('common.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              @click="onOrdersWithoutDepositActiveDialogConfirm()"
            >
              {{ $t('common.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </dialog-wrapper>
  </v-layout>
</template>

<script>
import moment from 'moment';
import TimeService from '@/services/TimeService';
import ErrorHandler from '@/services/ErrorHandler';
import NotificationService from '@/services/NotificationService';
import gql from 'graphql-tag';
import DialogWrapper from '../common/dialog-wrapper/DialogWrapper.vue';

export default {
  name: 'ManageUsersButton',
  components: { DialogWrapper },
  data: () => ({
    dialog: false,
    loading: false,
    savingUser: false,
    searchTimeout: null,
    items: [],
    searchQuery: '',
    selectedUser: null,
    ordersWithoutDepositUndoDialog: false,
    minDatePickerDate: moment().format(TimeService.dateFormat),
    cities: [{
      name: 'Kaunas',
      id: 1,
    }, {
      name: 'Vilnius',
      id: 2,
    }],
  }),
  watch: {
    searchQuery(newValue) {
      if (newValue && newValue.length >= 3) {
        this.searchUsers(newValue);
      }
    },
    dialog(newValue) {
      if (!newValue) {
        setTimeout(() => {
          this.selectedUser = null;
        }, 500);
      }
    },
  },
  methods: {
    async searchUsers(query) {
      this.loading = true;
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(async () => {
        try {
          const { data } = await this.$apollo.query({
            fetchPolicy: 'network-only',
            query: gql`query UsersSearchQuery($query: String!) {
              usersSearch(query: $query) {
                id
                displayName
                cityId
                admin
                email
                active
              }
            }`,
            variables: {
              query,
            },
          });

          this.items = data.usersSearch.map(u => ({
            id: u.id,
            displayName: u.displayName,
            cityId: u.cityId,
            admin: u.admin,
            email: u.email,
            active: u.active,
          }));
        } catch (error) {
          ErrorHandler.handleApolloError(error);
        } finally {
          this.loading = false;
        }
      }, 500);
    },
    async saveUser() {
      try {
        this.savingUser = true;
        await this.$apollo.mutate({
          mutation: gql`mutation UserSettingsMutation($data: UserSettings!) {
            saveUserSettings(data: $data)
          }`,
          variables: {
            data: {
              userId: this.selectedUser.id,
              displayName: this.selectedUser.displayName,
              cityId: this.selectedUser.cityId,
              admin: this.selectedUser.admin,
              active: this.selectedUser.active,
            },
          },
        });
        NotificationService.showSnackbar({
          message: 'settingsContainer.updatedUser',
          timeout: 5000,
          position: 'top',
        });
      } catch (error) {
        ErrorHandler.handleApolloError(error);
      } finally {
        this.savingUser = false;
        this.dialog = null;
      }
    },
  },
};
</script>

<style scoped>
  .user-disable-switch {
    bottom: 10px;
    position: absolute;
    left: 20px;
    width: 35%;
  }

  .user-disable-switch >>> .input-group__details {
    display: none;
  }
</style>
